import { Parser, Language } from 'web-tree-sitter';
import type { LanguageId } from '../pipeline/types';

// The wasm runtime and each grammar are imported with `?url` so Vite bundles
// them as ordinary hashed static assets (no custom WASM plugin needed) and
// only fetches a grammar the first time a file of that language is touched.
import treeSitterCoreWasmUrl from 'web-tree-sitter/web-tree-sitter.wasm?url';

// Grammars come from @vscode/tree-sitter-wasm (Microsoft, actively maintained)
// rather than the more commonly-referenced `tree-sitter-wasms` package: the
// latter is still built against tree-sitter-cli ^0.20.8 and fails to load
// under current web-tree-sitter with a dylink-metadata error — a real ABI
// incompatibility, not just a test artifact (see tests/extract-construct.test.ts).
const GRAMMAR_LOADERS: Record<LanguageId, () => Promise<{ default: string }>> = {
  javascript: () => import('@vscode/tree-sitter-wasm/wasm/tree-sitter-javascript.wasm?url'),
  typescript: () => import('@vscode/tree-sitter-wasm/wasm/tree-sitter-typescript.wasm?url'),
  python: () => import('@vscode/tree-sitter-wasm/wasm/tree-sitter-python.wasm?url'),
};

let initPromise: Promise<void> | null = null;
function ensureInit(): Promise<void> {
  if (!initPromise) {
    initPromise = Parser.init({
      locateFile: () => treeSitterCoreWasmUrl,
    });
  }
  return initPromise;
}

const languageCache = new Map<LanguageId, Promise<Language>>();

function loadLanguage(id: LanguageId): Promise<Language> {
  let cached = languageCache.get(id);
  if (!cached) {
    cached = ensureInit()
      .then(() => GRAMMAR_LOADERS[id]())
      .then((mod) => Language.load(mod.default));
    languageCache.set(id, cached);
  }
  return cached;
}

const parserCache = new Map<LanguageId, Promise<Parser>>();

/** Returns a ready-to-use Parser configured for the given language, loading and caching its grammar lazily. */
export function getParserFor(id: LanguageId): Promise<Parser> {
  let cached = parserCache.get(id);
  if (!cached) {
    cached = (async () => {
      await ensureInit();
      const language = await loadLanguage(id);
      const parser = new Parser();
      parser.setLanguage(language);
      return parser;
    })();
    parserCache.set(id, cached);
  }
  return cached;
}
